import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Select, MenuItem } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { getUserChallenges } from 'store/userChallenges/thunks'
import { DataGrid, GridValidRowModel, GridPaginationModel } from '@mui/x-data-grid'
import { useGetChallengeColumns } from '../../hooks/useGetChallengeColumns'
import { dataGridStyles } from 'theme/dataGridTheme'
import { useSelector } from 'react-redux'
import { selectUserChallenges } from 'store/userChallenges/slice'
import { localizedDate, localizedDateTime } from 'utils/formatDate'
import { useLocation } from 'react-router-dom'
import UserChallengeRounds from 'components/userChallenge/UserChallengeRounds'
import { rem } from 'theme/materialUITheme'
import { UserChallenge } from 'api/types'

const useStyles = makeStyles(() => ({
  evenRow: {
    backgroundColor: '#F5F5F9',
  },
  timeZoneNote: {
    color: '#8A8A8E',
    fontSize: 15,
    fontFamily: 'Söhne',
    fontWeight: '400',
    wordWrap: 'break-word',
    marginTop: '-2.1rem',
    marginLeft: '1rem',
    display: 'block',
  },
}))
interface Props {
  userState: UserState
}

export default function UserChallenges({ userState }: Props) {
  const intl = useIntl()
  const classes = useStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const [challengeRoundsOpen, setChallengeRoundsOpen] = useState<boolean>(false)
  const [currentChallengeId, setCurrentChallengeId] = useState<string | null>(null)
  const [selectedChallengeType, setSelectedChallengeType] = useState<string>(
    location.state?.selectedChallengeType || 'sponsored',
  )
  const { userChallenges = [], userChallengesStatus } = useSelector(selectUserChallenges)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const { user, status } = userState
  useEffect(() => {
    if (location.state?.selectedChallengeType) {
      setSelectedChallengeType(location.state.selectedChallengeType)
    }
  }, [location.state])

  useEffect(() => {
    if (location?.state?.from) {
      setChallengeRoundsOpen(true)
      setCurrentChallengeId(location.state.challengeId)
    }
  }, [location.state])

  const { getColumnsByChallengeType } = useGetChallengeColumns({
    intl,
    selectedChallengeType,
    onShowRounds: (challengeId: string) => {
      setCurrentChallengeId(challengeId)
      setChallengeRoundsOpen(true)
    },
  })

  const getChallenges = useCallback(() => {
    if (user.id) {
      dispatch(
        getUserChallenges({
          id: user.id,
          category: selectedChallengeType === 'sponsored' ? 'CHALLENGE_CATEGORY_SPONSORED' : 'CHALLENGE_CATEGORY_USER',
        }),
      )
    }
  }, [dispatch, user.id, selectedChallengeType])

  useEffect(() => {
    if (user.id) {
      getChallenges()
    }
  }, [getChallenges, user.id])

  useEffect(() => {
    // Reset pagination when challenge type changes
    setPaginationModel({ page: 0, pageSize: 25 })
  }, [selectedChallengeType])

  const mapUserChallengesToColumns = useMemo(() => {
    if (status === 'loading' || userChallengesStatus === 'loading') {
      return []
    }

    return userChallenges
      .map((challenge: UserChallenge) => {
        if (selectedChallengeType === 'userCreated') {
          return {
            id: challenge.challengeId,
            name: { id: challenge.challengeId, name: challenge.info?.name },
            type: challenge.typeConfig?.type,
            participantCount: challenge.participantCount,
            rounds: {
              challengeId: challenge.challengeId,
              participantId: challenge.participant?.participantId ?? null,
            },
            date: `${localizedDate(new Date(challenge.schedule?.startAt ?? ''), intl.locale, true)} -
          ${localizedDate(new Date(challenge.schedule?.endAt ?? ''), intl.locale, true)}`,
            status: { computedState: challenge.computedState },
          }
        }

        return {
          id: challenge.challengeId,
          name: { id: challenge.challengeId, name: challenge.info?.name ?? '' },
          type: challenge.typeConfig?.type,
          creator: { creator: challenge.createdBy },
          score: Number.isFinite(challenge.participant?.score) ? challenge.participant?.score : '-',
          date: `${localizedDate(new Date(challenge.schedule?.startAt ?? ''), intl.locale, true)} -
        ${localizedDate(new Date(challenge.schedule?.endAt ?? ''), intl.locale, true)}`,
          joined: challenge.participant?.acceptedAt
            ? localizedDateTime(new Date(challenge.participant.acceptedAt), intl.locale, true)
            : '-',
          rounds: {
            challengeId: challenge.challengeId,
            participantId: challenge.participant?.participantId ?? null,
          },
          participantStatus: challenge.participant?.status,
          status: { computedState: challenge.computedState },
        }
      })
      .filter((challenge) => challenge !== null) as GridValidRowModel[]
  }, [userChallenges, selectedChallengeType, status, userChallengesStatus])

  const currentChallenge = useMemo(() => {
    return userChallenges.filter((ch: UserChallenge) => ch.challengeId === currentChallengeId)[0]
  }, [userChallenges, currentChallengeId, selectedChallengeType])

  const closeChallengeRounds = () => {
    setChallengeRoundsOpen(false)
    setCurrentChallengeId(null)
  }

  // The page header above already names the user, so the rounds view only needs the challenge
  if (challengeRoundsOpen && currentChallenge) {
    return (
      <UserChallengeRounds
        userId={user.id ?? location.state.userId ?? -1}
        participantId={currentChallenge.participant?.participantId ?? ''}
        currentChallenge={currentChallenge}
        backLabel={intl.formatMessage({ id: 'strings.backToChallenges' })}
        close={closeChallengeRounds}
        refreshChallenges={getChallenges}
      />
    )
  }

  return (
    <>
      <Select value={selectedChallengeType} onChange={(e) => setSelectedChallengeType(e.target.value)} sx={{ mb: 1 }}>
        <MenuItem value="sponsored">{intl.formatMessage({ id: 'strings.sponsoredChallenges' })}</MenuItem>
        <MenuItem value="userCreated">{intl.formatMessage({ id: 'strings.userCreatedChallenges' })}</MenuItem>
        <MenuItem value="userJoined">{intl.formatMessage({ id: 'strings.userJoinedChallenges' })}</MenuItem>
      </Select>
      <DataGrid
        key={selectedChallengeType}
        autoHeight
        rows={mapUserChallengesToColumns}
        columns={getColumnsByChallengeType(selectedChallengeType)}
        loading={userChallengesStatus === 'loading' || status === 'loading'}
        sx={{
          '.MuiDataGrid-columnHeaderTitleContainer': { fontSize: rem(13), textTransform: 'uppercase' },
          ...dataGridStyles,
        }}
        rowSelection={false}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? classes.evenRow : '')}
        // getUserChallenges follows the API cursor and loads every page, so paging here is client side
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        disableColumnFilter
        disableColumnSorting
        disableColumnMenu
      />
      <span className={classes.timeZoneNote}>{intl.formatMessage({ id: 'strings.allTimesShownInUTC' })}</span>
    </>
  )
}
